import {
  Button,
  Divider,
  SelectBox,
  TextInput,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { Download, ExternalLink } from "lucide-react";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import moment from "moment";
import { useEffect, useState } from "react";
import { getLicenseInfo, getOrgInfo, userApi } from "../../api";
import Loader from "../../components/Loader";
import { getBaseURL, getRelayDomain } from "../../api/connection";
import { useSnackbar } from "notistack";
import { testHostIP } from "../InitialSetup/SelectApiMode";
import { useAuth } from "../../contexts/AuthContext";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import RelayAPI from "../../api/RelayAPI/index.js";
import usePlatform from "../../hooks/usePlatform";
import convertBytesToSize from "@/utils/convertBytesToSize";
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT } from "@/utils/date-format";
import {
  RelayTenant,
  resolveStorageLimitBytes,
  useGetSystemInfo,
} from "@/Features";
import type { License } from "@/types";

export interface Organization {
  address: string;
  city: string;
  email: string;
  fax: string;
  image: string;
  mobile: string;
  name: string;
  org_id: number;
  phone: string;
  state: string;
  website: string;
  zipcode: string;
}

const OrgProfile = ({ orgInfo }: { orgInfo: Organization }) => {
  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Organization Profile
      </div>
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: 200,
            height: 85,
          }}
        >
          <img
            src={orgInfo.image}
            style={{
              objectFit: "contain",
              width: "100%",
              height: "100%",
            }}
            alt="Organization Logo"
          ></img>
        </div>
        <div style={{ marginLeft: 20 }}>
          <div>{orgInfo.name}</div>
          <div>{orgInfo.address}</div>
          <div>
            {orgInfo.city || "City"}, {orgInfo.state || "State/Province"}{" "}
            {orgInfo.zipcode || "Postal Code"}
          </div>
          <div>
            {orgInfo.phone || "Phone"} | {orgInfo.email || "Email"}
          </div>
          <div>{orgInfo.website || "Website URL"}</div>
        </div>
      </div>
      <Divider style={{ margin: "20px 0px" }} />
    </>
  );
};

const LicenseInfo = ({ licenseInfo }: { licenseInfo: License }) => {
  const { isOnPremises } = usePlatform();

  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Subscription Info
      </div>
      <div>Registered To</div>
      <div style={{ marginLeft: 10, marginTop: 10, marginBottom: 20 }}>
        <div>{licenseInfo.full_name}</div>
        <div>{licenseInfo.organization}</div>
        <div>{licenseInfo.email}</div>
      </div>
      <div>Details</div>
      <div style={{ marginLeft: 10, marginTop: 10, marginBottom: 20 }}>
        <div>Licensed Users: {licenseInfo.user_licenses}</div>
        <div>User Seats Used: {licenseInfo.total_active_users}</div>
        <div>
          Subscription Expires:{" "}
          {moment(licenseInfo.expire_date).format("MMMM DD, YYYY")}
        </div>
      </div>
      {!isOnPremises && (
        <>
          <div>Cloud Storage</div>
          <StorageStatus licenseInfo={licenseInfo} />
        </>
      )}
      <RelayInfo />
      {!isOnPremises && (
        <>
          <div>Workspaces</div>
          {<WorkspacesList />}
        </>
      )}
      <Divider style={{ margin: "20px 0px" }} />
    </>
  );
};

const StorageStatus = styled(
  ({
    className,
    licenseInfo,
  }: {
    className?: string;
    licenseInfo: License;
  }) => {
    const { data: systemInfo } = useGetSystemInfo();

    const { files } = systemInfo || { files: { total_size: 0 } };

    // Same conversion as the upload pre-check so the meter and the check
    // agree; null means the license has no hot storage cap
    const hotStorageLimitBytes = resolveStorageLimitBytes(
      licenseInfo.hot_storage_limit_gb
    );

    const hotStorageUsed = convertBytesToSize(files?.total_size || 0);

    const percentageUsedHot =
      (hotStorageLimitBytes
        ? (((files?.total_size || 0) / hotStorageLimitBytes) * 100).toFixed(2)
        : "0.00") + "%";

    const meterHeader =
      hotStorageLimitBytes === null
        ? `${hotStorageUsed} Used (Unlimited)`
        : `${hotStorageUsed} of ${convertBytesToSize(
            hotStorageLimitBytes
          )} Used (${percentageUsedHot})`;

    return (
      <div className={className}>
        {/* HOT STORAGE */}
        <div>
          <div className="storage-meter-header">Hot Storage</div>
          <div className="storage-meter-header">{meterHeader}</div>
          <div className="storage-meter">
            <div className="outer-container"></div>
            <div
              className="inner-container"
              style={{ width: percentageUsedHot }}
            ></div>
          </div>
        </div>
      </div>
    );
  }
)`
  margin-top: 10px;
  margin-bottom: 20px;
  padding-left: 10px;

  & .storage-meter-header {
    font-size: 12px;
    margin-bottom: 5px;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-weight: 500;
  }

  & .storage-meter {
    position: relative;
    height: 8px;
    width: 250px;
  }

  & .outer-container {
    border-radius: 5px;
    border: 1px solid transparent;
    background-color: ${({ theme }) => theme.palette.action.hover};
    width: 100%;
    height: 100%;
  }

  & .inner-container {
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 5px;
    background-color: ${({ theme }) => theme.palette.primary.main};
    height: 100%;
    transition: width 0.2s ease;
  }
`;

const DateFormat = ({}) => {
  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Date & Time Format
      </div>
      <div
        style={{
          marginLeft: 10,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <SelectBox
          label="Date Format"
          size="sm"
          variant="outlined"
          width={220}
          data={["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"]}
          defaultValue={
            window.localStorage.getItem("dateFormat.date") ||
            DEFAULT_DATE_FORMAT
          }
          onChange={(value) => {
            window.localStorage.setItem("dateFormat.date", value);
          }}
        />
        <SelectBox
          label="Time Format"
          size="sm"
          variant="outlined"
          width={220}
          data={["HH:mm:ss", "h:mm:ss A"]}
          defaultValue={
            window.localStorage.getItem("dateFormat.time") ||
            DEFAULT_TIME_FORMAT
          }
          onChange={(value) => {
            window.localStorage.setItem("dateFormat.time", value);
          }}
        />
      </div>
      <Divider style={{ margin: "20px 0px" }} />
    </>
  );
};

const CurrencyFormat = ({}) => {
  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Currency Format
      </div>
      <div style={{ marginLeft: 10 }}>
        <SelectBox
          label="Select Currency"
          size="sm"
          variant="outlined"
          width={220}
          data={["USD", "EUR", "GBP", "CAD", "AUD", "NZD", "JPY", "CNY"]}
          defaultValue={window.localStorage.getItem("currencyFormat") || "USD"}
          onChange={(value) => {
            window.localStorage.setItem("currencyFormat", value);
          }}
        />
      </div>
      <Divider style={{ margin: "20px 0px" }} />
    </>
  );
};

const MonolithDesktopInstallers = ({}) => {
  const theme = useTheme();
  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "10px" }}>
        Monolith Desktop Installers
      </div>
      <div style={{ marginLeft: 10, color: theme.palette.text.secondary }}>
        Install Monolith as a Desktop client. The client is identical to the web
        app, but its in a dedicated Windows or MacOS application.
      </div>
      <div style={{ marginLeft: 10, marginTop: 20, marginBottom: 20 }}>
        <div style={{ display: "flex" }}>
          <div style={{ marginRight: 10 }}>
            <Button
              href="https://monolith-cloud.nyc3.digitaloceanspaces.com/updates/monolith-desktop/latest/Monolith%20Desktop%20Setup.exe"
              title="Download"
              variant="outlined"
              size="sm"
              leftSection={<Download size={16} />}
            >
              Windows
            </Button>
          </div>
          <div style={{ marginRight: 10 }}>
            <Button
              href="https://monolith-cloud.nyc3.digitaloceanspaces.com/updates/monolith-desktop/latest/Monolith%20Desktop%20Setup.dmg"
              title="Download"
              variant="outlined"
              size="sm"
              leftSection={<Download size={16} />}
            >
              MacOS (Intel)
            </Button>
          </div>
          <div style={{ marginRight: 10 }}>
            <Button
              href="https://monolith-cloud.nyc3.digitaloceanspaces.com/updates/monolith-desktop/latest/Monolith%20Desktop%20Setup%20arm64.dmg"
              title="Download"
              variant="outlined"
              size="sm"
              leftSection={<Download size={16} />}
            >
              MacOS (Apple Silicon)
            </Button>
          </div>
        </div>
      </div>
      <Divider style={{ margin: "20px 0px" }} />
    </>
  );
};

const apiHostSchema = z.object({
  api_host_ip: z
    .string()
    .min(1, "API Host Endpoint is required")
    .refine((value) => !value || testHostIP(value), {
      message: "Cannot reach API host endpoint",
    }),
});

const UpdateApiEndpoint = ({}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUpdating, setIsUpdating] = useState(false);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: { api_host_ip: getBaseURL() },
  });

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      const values = form.getValues();
      const validationResult = await validateAsync(apiHostSchema)(values);
      if (validationResult?.hasErrors) {
        form.setErrors(validationResult.errors);
        return;
      }

      window.electronAPI.setSettings("API_HOST", values.api_host_ip);
      window.localStorage.setItem("API_HOST", values.api_host_ip);
      enqueueSnackbar("API Endpoint Updated", { variant: "success" });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Update API Endpoint
      </div>
      <p>Update the endpoint used for your on-premises Monolith deployment.</p>
      <div style={{ marginLeft: 10, maxWidth: 400 }}>
        <TextInput
          size="sm"
          variant="outlined"
          label="API Host Endpoint"
          required
          placeholder="http://monolith_api_server_ip/api"
          style={{ width: 400 }}
          key={form.key("api_host_ip")}
          {...form.getInputProps("api_host_ip")}
        />
        <Button
          variant="contained"
          color="primary"
          size="sm"
          onClick={handleUpdate}
          loading={isUpdating}
          disabled={isUpdating}
          style={{ marginTop: 20 }}
        >
          Update
        </Button>
      </div>
      <Divider style={{ margin: "20px 0px" }} />
    </>
  );
};

const WorkspacesList = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [workspaceList, setWorkspaceList] = useState<
    | {
        tenant_slug: string;
        workspace: string;
      }[]
    | null
  >(null);

  const handleClick = (workspace: {
    tenant_slug: string;
    workspace: string;
  }) => {
    localStorage.setItem("MONOLITH.WORKSPACE", JSON.stringify(workspace));
    window.location.reload();
  };

  useEffect(() => {
    userApi.getUserWorkspaces(currentUser.email).then(setWorkspaceList);
  }, []);

  if (!workspaceList) return <Loader />;

  return (
    <div style={{ marginLeft: 10, marginTop: 10, marginBottom: 20 }}>
      <div style={{ color: theme.palette.text.secondary }}>
        These are the Monolith databases you have access to. Click on a
        workspace name to switch workspaces.
      </div>
      {workspaceList.map((workspace) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: theme.palette.primary.main,
              cursor: "pointer",
              margin: "10px 0px",
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.textDecoration = "none";
            }}
            onClick={() => {
              handleClick(workspace);
            }}
          >
            {workspace.workspace}
            <ExternalLink
              size={15}
              style={{
                marginLeft: 5,
                borderRadius: 4,
                padding: "1px",
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

const RelayInfo = styled(({ className }) => {
  const { currentUser } = useAuth();

  const tenant = currentUser.licenseInfo.tenant;

  const { isLoading, data } = useQuery({
    queryKey: ["relayInfo"],
    queryFn: () => RelayAPI.getRelayInfo({ cloud_tenant_id: tenant }),
  });

  if (isLoading) return null;

  const [relayInfo] = data?.data || [];

  if (!relayInfo) return null;

  return (
    <div className={className}>
      <div>Relay URL</div>
      <div style={{ marginLeft: 10, marginTop: 10, marginBottom: 20 }}>
        <div className={"relay-description"}>
          <div>
            Relay is a companion web application that allows others outside of
            Monolith to make forensics requests and view the status of those
            requests.
          </div>
        </div>
        <RelayUrl relayInfo={relayInfo} />
      </div>
    </div>
  );
})`
  .relay-link {
    display: flex;
    align-items: center;
    color: ${(props) => props.theme.palette.primary.main};
    cursor: pointer;
    margin: 10px 0px;
    &:hover {
      text-decoration: underline;
    }
  }
  .relay-description {
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

export const RelayUrl: React.FC<{
  relayInfo: RelayTenant;
  urlString?: boolean;
}> = ({ relayInfo, urlString }) => {
  const { isElectron } = usePlatform();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isElectron) {
      e.preventDefault();
      const target = e.target as HTMLAnchorElement; // Cast to HTMLAnchorElement
      if (target.href) {
        window.electronAPI.openExternalUrl(target.href);
      }
    }
  };

  const relayUrl = getRelayDomain() + "/" + relayInfo.tenant_slug;

  if (urlString) {
    return relayUrl;
  }

  return (
    <div className="relay-link">
      <a
        onClick={handleClick}
        href={relayUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {relayUrl}
      </a>
      <ExternalLink
        size={15}
        style={{
          marginLeft: 5,
          borderRadius: 4,
          padding: "1px",
        }}
      />
    </div>
  );
};

const SystemSettings = styled(({ className }) => {
  const [orgInfo, setOrgInfo] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const { isElectron, isOnPremises } = usePlatform();

  useEffect(() => {
    getOrgInfo().then((result) => {
      let arrayBufferView = new Uint8Array(result.image.data);
      let blob = new Blob([arrayBufferView]);
      let urlCreator = window.URL || window.webkitURL;
      let imageUrl = urlCreator.createObjectURL(blob);

      setOrgInfo({ ...result, image: imageUrl });
    });

    getLicenseInfo().then((result) => {
      setLicenseInfo(result);
    });
  }, []);

  if (!orgInfo || !licenseInfo) return <Loader />;

  return (
    <div className={className}>
      <div style={{ maxWidth: 750, padding: "0px 30px" }}>
        <OrgProfile orgInfo={orgInfo} />
        <LicenseInfo licenseInfo={licenseInfo} />
        {isElectron && isOnPremises && <UpdateApiEndpoint />}
        <MonolithDesktopInstallers />
        <DateFormat />
        <CurrencyFormat />
      </div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

export default SystemSettings;
